import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Circle } from "lucide-react";
import * as React from "react";

import { CheckMark, ChevronRight } from "@/assets/icons";
import { generateValidClassNameHandler } from "@/lib/utils";

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubTrigger = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & { inset?: boolean }
>(({
    children,
    className,
    inset,
    ...props
}, ref) => (
    <DropdownMenuPrimitive.SubTrigger
        ref={ref}
        className={generateValidClassNameHandler(
            "flex cursor-pointer select-none items-center rounded px-2 py-1.5 text-xs md:text-sm outline-none focus:bg-gray-100 data-[state=open]:bg-gray-100",
            inset && "pl-8",
            className,
        )}
        {...props}
    >
        {children}
        <ChevronRight
            className="ml-auto"
            size={16}
        />
    </DropdownMenuPrimitive.SubTrigger>
));

DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({
    className,
    ...props
}, ref) => (
    <DropdownMenuPrimitive.SubContent
        ref={ref}
        className={generateValidClassNameHandler(
            "z-50 min-w-[8rem] overflow-hidden rounded border bg-card p-1 shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            className,
        )}
        {...props}
    />
));

DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({
    className,
    sideOffset = 4,
    ...props
}, ref) => (
    <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
            ref={ref}
            sideOffset={sideOffset}
            className={generateValidClassNameHandler(
                "z-50 min-w-[8rem] overflow-hidden rounded border bg-card p-1 shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
                className,
            )}
            {...props}
        />
    </DropdownMenuPrimitive.Portal>
));

DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & { inset?: boolean }
>(({
    className,
    inset,
    ...props
}, ref) => (
    <DropdownMenuPrimitive.Item
        ref={ref}
        className={generateValidClassNameHandler(
            "relative flex cursor-pointer gap-2 select-none items-center rounded px-2 py-1.5 text-xs md:text-sm outline-none transition-colors focus:bg-gray-100 focus:text-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
            inset && "pl-8",
            className,
        )}
        {...props}
    />
));

DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({
    checked,
    children,
    className,
    ...props
}, ref) => (
    <DropdownMenuPrimitive.CheckboxItem
        checked={checked}
        ref={ref}
        className={generateValidClassNameHandler(
            "relative flex cursor-default select-none items-center rounded py-1.5 pl-8 pr-2 text-xs md:text-sm outline-none transition-colors focus:bg-gray-100 focus:text-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
            className,
        )}
        {...props}
    >
        <span className="absolute left-2 flex size-3.5 items-center justify-center">
            <DropdownMenuPrimitive.ItemIndicator>
                <CheckMark size={16} />
            </DropdownMenuPrimitive.ItemIndicator>
        </span>
        {children}
    </DropdownMenuPrimitive.CheckboxItem>
));

DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({
    children,
    className,
    ...props
}, ref) => (
    <DropdownMenuPrimitive.RadioItem
        ref={ref}
        className={generateValidClassNameHandler(
            "relative flex cursor-default select-none items-center rounded py-1.5 pl-8 pr-2 text-xs md:text-sm outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
            className,
        )}
        {...props}
    >
        <span className="absolute left-2 flex size-3.5 items-center justify-center">
            <DropdownMenuPrimitive.ItemIndicator>
                <Circle
                    className="fill-current"
                    size={8}
                />
            </DropdownMenuPrimitive.ItemIndicator>
        </span>
        {children}
    </DropdownMenuPrimitive.RadioItem>
));

DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Label>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & { inset?: boolean }
>(({
    className,
    inset,
    ...props
}, ref) => (
    <DropdownMenuPrimitive.Label
        ref={ref}
        className={generateValidClassNameHandler(
            "px-2 py-1.5 text-xs md:text-sm font-semibold",
            inset && "pl-8",
            className,
        )}
        {...props}
    />
));

DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({
    className,
    ...props
}, ref) => (
    <DropdownMenuPrimitive.Separator
        ref={ref}
        className={generateValidClassNameHandler(
            "-mx-1 my-1 h-px bg-gray-300",
            className,
        )}
        {...props}
    />
));

DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({
    className,
    ...props
} : React.HTMLAttributes<HTMLSpanElement>) => (
    <span
        className={generateValidClassNameHandler(
            "ml-auto text-xs tracking-widest opacity-60",
            className,
        )}
        {...props}
    />
);

DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

export {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuShortcut,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
};
